import { clsx, type ClassValue } from 'clsx';
import { format } from 'date-fns';
import { twMerge } from 'tailwind-merge';

export const cn = (...inputs: ClassValue[]) => {
  return twMerge(clsx(inputs));
};

export interface WhatsAppFormattedText {
  type: 'text' | 'bold' | 'italic' | 'strikethrough' | 'monospace';
  content: string;
}

export const parseWhatsAppFormatting = (
  text: string,
): WhatsAppFormattedText[] => {
  if (!text) return [];

  const parts: WhatsAppFormattedText[] = [];
  let currentIndex = 0;

  const patterns = [
    // eslint-disable-next-line
    { regex: /\*([^\*\n]+?)\*/g, type: 'bold' as const }, // *bold*
    { regex: /_([^_\n]+?)_/g, type: 'italic' as const }, // _italic_
    { regex: /~([^~\n]+?)~/g, type: 'strikethrough' as const }, // ~strikethrough~
    { regex: /```([^`]+?)```/g, type: 'monospace' as const }, // ```monospace```
  ];

  // Find all matches with their positions
  const matches: {
    start: number;
    end: number;
    type: WhatsAppFormattedText['type'];
    content: string;
    fullMatch: string;
  }[] = [];

  for (const pattern of patterns) {
    let match;
    while ((match = pattern.regex.exec(text)) !== null) {
      matches.push({
        start: match.index,
        end: match.index + match[0].length,
        type: pattern.type,
        content: match[1], // The captured group (content without markers)
        fullMatch: match[0],
      });
    }
    // Reset regex lastIndex to avoid issues with global flag
    pattern.regex.lastIndex = 0;
  }

  // Sort matches by start position
  matches.sort((a, b) => a.start - b.start);

  // Remove overlapping matches (keep the first one found)
  const validMatches: typeof matches = [];
  for (const match of matches) {
    const hasOverlap = validMatches.some(
      (existing) => match.start < existing.end && match.end > existing.start,
    );
    if (!hasOverlap) {
      validMatches.push(match);
    }
  }

  // Build the result array
  for (const match of validMatches) {
    // Add text before this match
    if (currentIndex < match.start) {
      const beforeText = text.slice(currentIndex, match.start);
      if (beforeText) {
        parts.push({ type: 'text', content: beforeText });
      }
    }

    // Add the formatted match
    parts.push({ type: match.type, content: match.content });
    currentIndex = match.end;
  }

  // Add remaining text after the last match
  if (currentIndex < text.length) {
    const remainingText = text.slice(currentIndex);
    if (remainingText) {
      parts.push({ type: 'text', content: remainingText });
    }
  }

  if (parts.length === 0) {
    parts.push({ type: 'text', content: text });
  }

  return parts;
};

export const formatDate = (date: string, ...options: boolean[]) => {
  try {
    if (!date) return '--';
    let formattedDate = '';

    const [
      hasTime = true,
      minimal = false,
      hasWeekDay = false,
      onlyTime = false,
    ] = options;

    if (onlyTime) {
      formattedDate = format(new Date(date), 'hh:mm a');
      return formattedDate;
    }

    if (hasTime && !minimal) {
      formattedDate = format(new Date(date), 'MMM d, yyyy - hh:mm a');
    } else if (hasTime && minimal) {
      formattedDate = format(new Date(date), 'dd/MM/yyyy - hh:mm a');
    } else if (!hasTime && minimal) {
      formattedDate = format(new Date(date), 'dd/MM/yyyy');
    } else if (hasWeekDay) {
      formattedDate = format(new Date(date), 'EEE, MMM d');
    } else {
      formattedDate = format(new Date(date), 'MMM d, yyyy');
    }
    return formattedDate;
  } catch (error) {
    console.error(error);
    return '--';
  }
};

export const formatTime = (timestamp: number | string) => {
  try {
    if (!timestamp) return '--';
    const now = new Date();
    // Handle both Unix timestamps (seconds) and ISOdate strings
    const date =
      typeof timestamp === 'string'
        ? new Date(timestamp)
        : new Date(timestamp * 1000);

    const diff = now.getTime() - date.getTime();

    if (isNaN(diff)) return '--';

    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    // if (minutes < 1) return 'now';
    if (minutes < 60) return `${String(minutes)}m ago`;
    if (hours < 24) return `${String(hours)}h ago`;
    return `${String(days)}d ago`;
  } catch (error) {
    console.error(error);
    return '--';
  }
};

export const getTagBadgeStyle = (color: string) => {
  color = color.toLowerCase();

  switch (color) {
    case 'teal':
      return 'bg-teal-100 border-teal-300 text-teal-800 rounded-sm';
    case 'yellow':
      return 'bg-yellow-100 border-yellow-300 text-yellow-800 rounded-sm';
    case 'orange':
      return 'bg-orange-100 border-orange-300 text-orange-800 rounded-sm';
    case 'pink':
      return 'bg-pink-100 border-pink-300 text-pink-800 rounded-sm';
    case 'purple':
      return 'bg-purple-100 border-purple-300 text-purple-800 rounded-sm';
    case 'blue':
      return 'bg-blue-100 border-blue-300 text-blue-800 rounded-sm';
    case 'green':
      return 'bg-green-100 border-green-300 text-green-800 rounded-sm';
    case 'red':
      return 'bg-red-100 border-red-300 text-red-800 rounded-sm';
    default:
      return 'bg-gray-100 border-gray-300 text-gray-800 rounded-sm';
  }
};

export const avatarSvgs = Array.from(
  { length: 22 },
  (_, i) => `/avatar_svgs/${(i + 1).toString()}.svg`,
);

export const capitalize = (value: string | null | undefined) => {
  if (value === null || value === undefined) return '';

  return value.charAt(0).toUpperCase() + value.slice(1);
};
